import { AnalyticsLibraryProps, FeatureFlagsResponse } from 'Common/interfaces';
import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react';
import { useQuery } from 'react-query';
import { getAnonymousId } from 'Common/analytics';

export const TOOLKIT_API_URL = `/toolkit/api`;

export interface ToolkitApiResult<T> {
  isLoading: boolean;
  data: T;
  error: any;
}

export interface UseToolkitQueryOptions<T> {
  requestInit?: RequestInit;
  transform?: (response: Response) => Promise<T>;
  baseUrl?: string;
}

export interface RequestOptions<T> extends UseToolkitQueryOptions<T> {
  requestInit?: RequestInit;
  transform?: (response: Response) => Promise<T>;
  baseUrl?: string;
  auth0?: Auth0ContextInterface;
}

function joinUrl(baseUrl: string, path: string) {
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return normalizedBaseUrl + normalizedPath;
}

async function updateRequestInit(
  method: string,
  requestInit: RequestInit,
  auth0?: Auth0ContextInterface
) {
  requestInit = requestInit ?? {};

  let updatedRequestInit: RequestInit = {
    ...requestInit,
    method: method,
    headers: {
      ...(requestInit.headers ?? {}),
    },
  };

  if (auth0?.isAuthenticated) {
    try {
      const accessToken = await auth0.getAccessTokenSilently();

      if (accessToken) {
        updatedRequestInit['headers'] = {
          ...(updatedRequestInit.headers ?? {}),
          Authorization: 'Bearer ' + accessToken,
        };
      }
    } catch (er) {
      if (er.error === 'missing_refresh_token') {
        auth0.logout({ logoutParams: { returnTo: window.location.origin } });
      }
    }
  } else {
    updatedRequestInit['headers'] = {
      ...(updatedRequestInit.headers ?? {}),
      'Anonymous-Id': `${getAnonymousId()}`,
    };
  }

  return updatedRequestInit;
}

function defaultRequestOptions<T>(
  options?: RequestOptions<T>
): RequestOptions<T> {
  return {
    auth0: null,
    requestInit: {},
    transform: (response) => response.json(),
    baseUrl: TOOLKIT_API_URL,
    ...(options ?? {}),
  };
}

function addContentTypeIfMissing(
  contentType: string,
  requestInit?: RequestInit
): RequestInit {
  requestInit = requestInit ?? {};
  const headers = requestInit.headers ?? {};

  if (!headers['Content-Type']) {
    return {
      ...requestInit,
      headers: {
        ...requestInit.headers,
        'Content-Type': contentType,
      },
    };
  } else {
    return requestInit;
  }
}

export function useToolkitQuery<T>(
  path: string,
  options: UseToolkitQueryOptions<T> = {
    requestInit: {},
    transform: (response) => response.json(),
    baseUrl: TOOLKIT_API_URL,
  }
): ToolkitApiResult<T> {
  const auth0: Auth0ContextInterface = useAuth0();
  const { requestInit, transform, baseUrl } = options;

  const {
    isLoading,
    data,
    error: fetchError,
  } = useQuery(path, async () => {
    const updatedRequestInit = await updateRequestInit(
      'GET',
      requestInit,
      auth0
    );
    return await fetch(joinUrl(baseUrl, path), updatedRequestInit).then(
      (response) => transform(response)
    );
  });

  return {
    isLoading: isLoading,
    data: data,
    error: fetchError,
  };
}

export async function getRequest<T>(
  path: string,
  options?: RequestOptions<T>
): Promise<T> {
  const { auth0, requestInit, transform, baseUrl } =
    defaultRequestOptions(options);

  let updatedRequestInit = await updateRequestInit('GET', requestInit, auth0);

  return await fetch(joinUrl(baseUrl, path), updatedRequestInit).then(
    (response) => transform(response)
  );
}

export async function postRequest<T>(
  path: string,
  options?: RequestOptions<T>
): Promise<T> {
  const { auth0, requestInit, transform, baseUrl } =
    defaultRequestOptions(options);
  let updatedRequestInit = await updateRequestInit(
    'POST',
    addContentTypeIfMissing('application/json', requestInit),
    auth0
  );

  return await fetch(joinUrl(baseUrl, path), updatedRequestInit).then(
    (response) => transform(response)
  );
}

export async function putRequest<T>(
  path: string,
  options?: RequestOptions<T>
): Promise<T> {
  const { auth0, requestInit, transform, baseUrl } =
    defaultRequestOptions(options);
  let updatedRequestInit = await updateRequestInit(
    'PUT',
    addContentTypeIfMissing('application/json', requestInit),
    auth0
  );

  return await fetch(joinUrl(baseUrl, path), updatedRequestInit).then(
    (response) => transform(response)
  );
}

export function getFeatureFlags(): Promise<FeatureFlagsResponse> {
  return getRequest(`v1/features`);
}

export function getDashboards(): Promise<{
  dashboards: AnalyticsLibraryProps[];
}> {
  return getRequest(`v1/dashboards`);
}

export async function getAsciidoc(path: string): Promise<Response> {
  return await getRequest<Response>(path, {
    transform: (r) => new Promise(() => r),
    baseUrl: '/toolkit',
  });
}

export function logFrontendError(e: Error): Promise<void> {
  // todo useAuth0 or make the endpoint open
  return postRequest(`v1/log-frontend-error`, {
    requestInit: {
      body: JSON.stringify({
        level: 'error',
        message: e.message,
        trace: e.stack,
      }),
    },
  });
}
